import type { ParagraphAncestor } from './indexParagraphs';
import {
  collectAnchoredRuns,
  formatCommentEnd,
  formatCommentStart,
  serializeCommentThread,
} from '../serialize/commentMarkers';
import { wrapSection } from '../serialize/serializeCast';
import type { SerializeOptions } from '../serialize/serializeOptions';
import { serializeParagraph } from '../serialize/serializeParagraph';
import type { CastParagraph, CastRoot } from '../types';

export type EstimateTokens = (text: string) => number;

/**
 * The tokens each paragraph's comments add to the output: a start and an end
 * marker for every comment touching it, plus the whole thread on the paragraph
 * where the comment ends in the full document. Charging the thread to that one
 * paragraph keeps its cost the same whatever else gets included. When only an
 * earlier part of a comment is included, its thread still prints but was not
 * charged, so the result can run slightly over budget.
 */
export function estimateCommentCosts(
  root: CastRoot,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): Map<CastParagraph, number> {
  const costs = new Map<CastParagraph, number>();
  const threads = root.commentThreads;
  if (!serializeOptions.emitComments || !threads) return costs;

  const addCost = (paragraph: CastParagraph, cost: number): void => {
    costs.set(paragraph, (costs.get(paragraph) ?? 0) + cost);
  };

  for (const { runs, thread } of collectAnchoredRuns(root, threads).values()) {
    const marker = { continues: false, thread };
    const markerCost = estimateTokens(formatCommentStart(marker) + formatCommentEnd(marker));
    for (const paragraph of new Set(runs.map(run => run.paragraph))) addCost(paragraph, markerCost);

    const endParagraph = runs[runs.length - 1].paragraph;
    addCost(endParagraph, estimateTokens(`\n${serializeCommentThread(thread)}`));
  }

  return costs;
}

/**
 * Serializes a single paragraph as if it were the only thing being emitted,
 * regardless of its current `included` flag, then runs it through the
 * token estimator. Mirrors what the paragraph will actually contribute when
 * the wrapping section/table/etc. eventually emits.
 */
export function estimateParagraphCost(
  paragraph: CastParagraph,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): number {
  const probe: CastParagraph = { ...paragraph, included: true };
  const serialized = serializeParagraph(probe, serializeOptions) ?? '';
  return estimateTokens(serialized);
}

/**
 * Estimates the token overhead a container's open/close tags add when it emits
 * content: the tag pair plus the two newlines that bracket the content, i.e. the
 * wrapper with empty inner (`<tag>\n\n</tag>`).
 *
 * Sections go through `wrapSection` — the same code the serializer uses — so the
 * estimate tracks their `variant` and font attributes exactly and can't drift.
 * Table/row/cell tags are attribute-free, so a constant is already exact.
 */
export function estimateWrapperCost(
  ancestor: ParagraphAncestor,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): number {
  switch (ancestor.type) {
    case 'section':
      return estimateTokens(wrapSection(ancestor, serializeOptions, ''));
    case 'table':
      return estimateTokens('<table>\n\n</table>');
    case 'tableRow':
      return estimateTokens('<tr>\n\n</tr>');
    case 'tableCell':
      return estimateTokens('<td>\n\n</td>');
  }
}
